import { useCallback, useRef, useState } from 'react';

import { useLazyGetClientFileQuery } from '@app/api/ClientFiles';
import { useLazyGetSendoutFileQuery } from '@app/api/Sendouts/api';
import { mediaExtensions } from '@app/components/Chat/constants';
import {
  DOC,
  DOCX,
  FRONT_WARNING,
  JPEG,
  JPG,
  PDF,
  PNG,
  TXT,
  XLS,
  XLSX,
} from '@app/constants';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { AttachmentModes, type NClientFiles, type NFiles } from '@app/types';

const extensionMap: Record<string, string> = {
  PDF,
  XLS,
  XLSX,
  DOC,
  DOCX,
  TXT,
  JPEG,
  JPG,
  PNG,
};

export const useAttachments = (
  getFileEndpoint: AttachmentModes = AttachmentModes.Clients
) => {
  const [mediaFiles, setMediaFiles] = useState<Array<NClientFiles.MediaFiles>>(
    []
  );
  const [mediaFilesSizes, setMediaFilesSizes] = useState<
    Array<NClientFiles.MediaFilesSizes>
  >([]);
  const mediaFilesRef = useRef<Array<NClientFiles.MediaFiles>>([]);
  const addMediaFileRef = useCallback((value: NClientFiles.MediaFiles) => {
    mediaFilesRef.current = [...mediaFilesRef.current, value];
  }, []);
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const [getClientFile] = useLazyGetClientFileQuery();
  const [getSendoutFile] = useLazyGetSendoutFileQuery();

  const getImagePreview = useCallback(
    async (id: number) => {
      try {
        const response =
          getFileEndpoint === AttachmentModes.Clients
            ? await getClientFile({
                fileId: id,
                ActiveBranchId: activeBranchId,
              }).unwrap()
            : await getSendoutFile({
                fileId: id,
                ActiveBranchId: activeBranchId,
              }).unwrap();

        if (!response) {
          showNotification(
            `При загрузке превью изображения файл с идентификатором ${id} не был найден`,
            FRONT_WARNING
          );

          return;
        }

        const fileName =
          'FileName' in response ? response.FileName : response.Name;

        return {
          ...response,
          FileName: fileName,
        };
      } catch (error) {
        showNotification(error);
        return;
      }
    },
    [
      getFileEndpoint,
      getClientFile,
      getSendoutFile,
      activeBranchId,
      showNotification,
    ]
  );

  const downloadFile = useCallback(
    async (id: number, extension: string | null) => {
      const data = extension ? extensionMap[extension] : '';
      const clientServicesPage = document.querySelector('body');
      const imagePreview = await getImagePreview(id);

      if (!imagePreview)
        return showNotification(
          `Файл с идентификатором ${id} для скачивания не был найден`,
          FRONT_WARNING
        );

      const { Data, FileName } = imagePreview;
      const dataBase64 = Data;
      const downloadButton = document.createElement('a');

      downloadButton.setAttribute('href', `data:${data};base64,${dataBase64}`);
      downloadButton.setAttribute('download', FileName || 'file');
      downloadButton.setAttribute('style', 'display: none');

      clientServicesPage?.appendChild(downloadButton);
      downloadButton.click();

      clientServicesPage?.removeChild(downloadButton);
    },
    [getImagePreview, showNotification]
  );

  const getImageWidth = useCallback(
    async (base64String: string, id: number) => {
      const image = document.createElement('img');

      image.addEventListener('load', () => {
        setMediaFilesSizes((prev) => [
          ...prev,
          {
            Id: id,
            Height: image.height,
            Width: image.width,
          },
        ]);
      });

      image.src = base64String;
    },
    [setMediaFilesSizes]
  );

  const handleFile = useCallback(
    async ({
      Id,
      Extension,
    }: Pick<NFiles.ModelClientFile, 'Id' | 'Extension'>) => {
      if (
        Extension &&
        mediaExtensions.includes(Extension) &&
        !mediaFilesRef.current?.find(({ id }) => id === Id)
      ) {
        const response = await getImagePreview(Id);

        if (!response)
          return showNotification(
            `В процессе обработки файл с идентификатором ${Id} не был найден`,
            FRONT_WARNING
          );

        const previewData = response.Data;

        if (!previewData) {
          return showNotification(
            `В процессе обработки файл с идентификатором ${Id} не имеет содержимого`,
            FRONT_WARNING
          );
        }

        getImageWidth(`data:image/png;base64,${previewData}`, Id);
        addMediaFileRef({ id: Id, data: previewData });
        setMediaFiles((prev) => [
          ...prev,
          {
            id: Id,
            data: previewData,
          },
        ]);
      }
    },
    [addMediaFileRef, getImagePreview, getImageWidth, showNotification]
  );

  const getClientFileHandler = useCallback(
    async (fileId: number) => {
      try {
        const data = await getClientFile({
          fileId,
          ActiveBranchId: activeBranchId,
        }).unwrap();

        return data;
      } catch (error) {
        showNotification(error);
        return null;
      }
    },
    [activeBranchId, getClientFile, showNotification]
  );

  return {
    mediaFiles,
    mediaFilesSizes,
    downloadFile,
    getImagePreview,
    handleFile,
    setMediaFiles,
    setMediaFilesSizes,
    getClientFileHandler,
  };
};
